import { useCallback } from "react";

import { useGetStores } from "@/api";
import { useActiveStoreName } from "@/hooks/useActiveStoreName";
import { useDashboardDate } from "@/hooks/useDashboardDate";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useLatestOrders } from "@/hooks/useLatestOrders";
import { useOrderTrend } from "@/hooks/useOrderTrend";
import { useQuickOrder } from "@/hooks/useQuickOrder";
import { useCommission } from "@/hooks/useCommission";
import { useAuthStore } from "@/stores/auth.store";

export const DASHBOARD_QUICK_ORDER_SECTION_ID = "dashboard-quick-order";

export function useDashboard() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const date = useDashboardDate();
  const storeName = useActiveStoreName();
  const { stats, isLoading: isStatsLoading } = useDashboardStats();
  const { orders, isLoading: isOrdersLoading, isError: isOrdersError } = useLatestOrders();
  const { data: trendData, growthPercent, isLoading: isTrendLoading } = useOrderTrend();
  const quickOrder = useQuickOrder();

  const { data: storesData, isSuccess: isStoresLoaded } = useGetStores(undefined, {
    query: { enabled: isAuthenticated },
  });

  const stores = storesData?.data?.data ?? [];
  const hasNoShop = isStoresLoaded && stores.length === 0;

  const { isRestricted: isOrderCreateBlocked } = useCommission();

  const scrollToQuickOrder = useCallback(() => {
    document.getElementById(DASHBOARD_QUICK_ORDER_SECTION_ID)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  return {
    date,
    storeName,
    stats,
    isStatsLoading,
    orders,
    isOrdersLoading,
    isOrdersError,
    trendData,
    growthPercent,
    isTrendLoading,
    quickOrder,
    hasNoShop,
    isOrderCreateBlocked,
    scrollToQuickOrder,
    quickOrderSectionId: DASHBOARD_QUICK_ORDER_SECTION_ID,
  };
}
